import { Link } from "react-router-dom";
import { siteInfo } from "@/mocks/content";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const principles = [
  { icon: "ri-scales-3-line", title: "只做整理，不做煽动", desc: "对同一话题的不同说法保持并列呈现，不替任何人下结论，也不放大情绪。" },
  { icon: "ri-link-m", title: "来源可追溯", desc: "每条内容都标注发布时间与所属分类，方便你回到上下文里理解它。" },
  { icon: "ri-shield-check-line", title: "尊重当事人", desc: "涉及个人的内容，避免使用未经证实的描述，收到纠错反馈后优先处理。" },
  { icon: "ri-refresh-line", title: "持续修正", desc: "内容不是一次写完就结束，随着信息更新，我们会持续补充与修订。" },
];

const milestones = [
  { year: "起步", title: "从整理一个话题开始", desc: "最初只是把零散的现场记录按时间排好，方便自己和朋友回看。" },
  { year: "成型", title: "建立分类与专题", desc: "随着内容增多，逐步划分出娱乐、影视、社会、生活等主题分类。" },
  { year: "现在", title: "每日持续更新", desc: "形成固定的更新节奏，热门话题进入热料榜，方便快速翻看。" },
];

const reviews = [
  { name: "林间小路", role: "常读用户", text: "最喜欢它把同一件事按时间排好，不用在好几个地方来回翻，看完一遍就清楚多了。" },
  { name: "阿哲不加班", role: "内容爱好者", text: "分类做得很清楚，想找生活类的记录直接点进去就行，比乱刷信息流省时间。" },
  { name: "晚风Zoe", role: "长期读者", text: "更新很稳定，细节也比较克制，不会为了吸引点击把标题写得很夸张。" },
];

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们 - 看料91 内容聚合与整理",
    description:
      "了解看料91的品牌定位、发展理念与内容原则。我们聚合热门爆料与现场记录，坚持整理、核实与持续更新，让用户翻看、阅读、查看内容更省时间。",
    keywords: "看料91,关于我们,看料91阅读,看料91浏览,内容原则",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          name: "关于我们 - 看料91",
          url: "https://fxwlkj.cn/about",
          inLanguage: "zh-CN",
        },
        {
          "@type": "Organization",
          name: siteInfo.name,
          url: "https://fxwlkj.cn/",
          email: siteInfo.email,
          description: siteInfo.intro,
        },
      ],
    },
  });

  return (
    <>
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <section className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-background-100 rounded-lg px-5 md:px-10 py-10 md:py-14">
          <div className="lg:col-span-7">
            <span className="text-xs md:text-sm text-primary-700 font-medium">关于我们</span>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 mt-2 leading-snug">
              {siteInfo.name}
              <span className="block text-lg md:text-2xl font-bold text-secondary-700 mt-3">
                一个认真整理话题的阅读平台
              </span>
            </h1>
            <p className="text-sm md:text-base text-foreground-700 mt-6 leading-loose">
              {siteInfo.intro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="px-5 py-3 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                返回首页翻看
              </Link>
              <Link
                to="/contact"
                className="px-5 py-3 bg-background-50 border border-background-300 text-foreground-800 rounded-md text-sm hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                联系我们
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20stylized%20editorial%20still%20life%20of%20stacked%20notebooks%20and%20folded%20newspapers%20on%20a%20cream%20desk%20with%20soft%20amber%20lamp%20light%2C%20minimal%20clean%20background%2C%20high%20detail%2C%20harmonious%20warm%20composition&width=900&height=680&seq=kl-about-01&orientation=landscape"
                alt="看料91 内容整理与阅读场景配图"
                title="看料91 内容整理"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div>
            <span className="text-xs md:text-sm text-secondary-700 font-medium">网站定位</span>
            <h2 className="font-heading text-xl font-black text-foreground-950 mt-1">为谁而做</h2>
            <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
              为那些想快速了解一个话题、又不想被情绪裹挟的读者而做。我们把信息密度和阅读效率放在第一位。
            </p>
          </div>
          <div>
            <span className="text-xs md:text-sm text-secondary-700 font-medium">发展理念</span>
            <h2 className="font-heading text-xl font-black text-foreground-950 mt-1">慢一点，稳一点</h2>
            <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
              不追求第一时间刷屏，而是等信息相对清晰后再整理。宁可晚一点，也不希望误导读者。
            </p>
          </div>
          <div>
            <span className="text-xs md:text-sm text-secondary-700 font-medium">更新节奏</span>
            <h2 className="font-heading text-xl font-black text-foreground-950 mt-1">固定而不打扰</h2>
            <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
              每日在固定时段集中更新，让你养成稳定的阅读习惯，而不是被动接收零散的推送。
            </p>
          </div>
        </div>
      </section>

      <section id="principles" className="w-full px-4 md:px-8 scroll-mt-24">
        <div className="bg-secondary-900 rounded-lg px-5 md:px-10 py-12 md:py-16">
          <div className="max-w-2xl">
            <span className="text-xs md:text-sm text-accent-400 font-medium">内容原则</span>
            <h2 className="font-heading text-2xl md:text-3xl font-black text-white mt-2">
              我们如何对待每一条内容
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
            {principles.map((p) => (
              <div key={p.title} className="flex items-start gap-4 bg-secondary-800/70 rounded-lg p-5">
                <span className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md bg-accent-500/15 text-accent-400">
                  <i className={`${p.icon} text-2xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-white">{p.title}</h3>
                  <p className="text-sm text-secondary-100/75 mt-1.5 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mb-10">
          我们的成长轨迹
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {milestones.map((m) => (
            <div key={m.year} className="border-t-2 border-primary-500 pt-5">
              <span className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-800">
                {m.year}
              </span>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mt-4">{m.title}</h3>
              <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-16">
        <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mb-10">
          用户怎么说
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((r) => (
            <figure key={r.name} className="bg-background-100 rounded-lg p-6 flex flex-col">
              <span className="w-8 h-8 flex items-center justify-center text-accent-500">
                <i className="ri-double-quotes-l text-3xl" />
              </span>
              <blockquote className="text-sm text-foreground-700 leading-relaxed mt-3 flex-1">
                {r.text}
              </blockquote>
              <figcaption className="mt-5 pt-4 border-t border-background-200 text-sm text-foreground-800">
                {r.name}
                <span className="block text-xs text-foreground-500 mt-0.5">{r.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>
    </>
  );
}